/**
 * Generates Python SDK code snippets for fetching data from Roboto files
 */

interface FileTopicConfig {
  datasetId: string;
  filename: string;
  topics: Map<string, Set<string>>; // topic name -> fields
}

/**
 * Generates a Python SDK snippet for a single file
 */
function generateSingleFileSnippet(
  fileConfig: FileTopicConfig,
  startTimeSec: number,
  endTimeSec: number,
): string {
  const topicConfigsStr = Array.from(fileConfig.topics.entries())
    .map(([topic, fields]) => {
      const fieldsStr = Array.from(fields)
        .map((f) => `"${f}"`)
        .join(", ");
      return `    ("${topic}", [${fieldsStr}])`;
    })
    .join(",\n");

  return `from roboto import Dataset
import pandas as pd

ds = Dataset.from_id("${fileConfig.datasetId}")
file = ds.get_file_by_path("${fileConfig.filename}")

selection_start = ${startTimeSec.toFixed(9)}
selection_end = ${endTimeSec.toFixed(9)}

# Define topics and their fields to fetch
topic_configs = [
${topicConfigsStr}
]

# Fetch data from all topics and combine
df = pd.DataFrame()
for topic_name, fields in topic_configs:
    topic = file.get_topic(topic_name)
    temp_df = topic.get_data_as_df(fields, [], selection_start, selection_end)
    df = pd.concat([df, temp_df])

# Convert index to timestamp column
df = df.reset_index().rename(columns={'_index': 'timestamp'})
`;
}

/**
 * Generates a Python SDK snippet for multiple files
 */
function generateMultiFileSnippet(
  fileConfigs: FileTopicConfig[],
  startTimeSec: number,
  endTimeSec: number,
): string {
  const fileConfigsStr = fileConfigs
    .map((fileConfig) => {
      const topicConfigsStr = Array.from(fileConfig.topics.entries())
        .map(([topic, fields]) => {
          const fieldsStr = Array.from(fields)
            .map((f) => `"${f}"`)
            .join(", ");
          return `        ("${topic}", [${fieldsStr}])`;
        })
        .join(",\n");

      return `    {
        "dataset_id": "${fileConfig.datasetId}",
        "file_path": "${fileConfig.filename}",
        "topics": [
${topicConfigsStr}
        ]
    }`;
    })
    .join(",\n");

  return `from roboto import Dataset
import pandas as pd

selection_start = ${startTimeSec.toFixed(9)}
selection_end = ${endTimeSec.toFixed(9)}

# Define files and their topics to fetch
file_configs = [
${fileConfigsStr}
]

# Fetch data from all files and topics
df = pd.DataFrame()
for file_config in file_configs:
    ds = Dataset.from_id(file_config["dataset_id"])
    file = ds.get_file_by_path(file_config["file_path"])
    
    for topic_name, fields in file_config["topics"]:
        topic = file.get_topic(topic_name)
        temp_df = topic.get_data_as_df(fields, [], selection_start, selection_end)
        temp_df = temp_df.add_prefix(f"{file_name}.")

        df = pd.concat([df, temp_df])

# Convert index to timestamp column
df = df.reset_index().rename(columns={'_index': 'timestamp'})
`;
}

/**
 * Generates a placeholder Python SDK snippet
 */
function generatePlaceholderSnippet(
  startTimeSec: number,
  endTimeSec: number,
): string {
  return `from roboto import Dataset
import pandas as pd

ds = Dataset.from_id("<dataset_id>")
file = ds.get_file_by_path("<file_name>")

selection_start = ${startTimeSec.toFixed(9)}
selection_end = ${endTimeSec.toFixed(9)}

# Define topics and their fields to fetch
topic_configs = [
    ("/example/topic", ["field1", "field2"])
]

# Fetch data from all topics and combine
df = pd.DataFrame()
for topic_name, fields in topic_configs:
    topic = file.get_topic(topic_name)
    temp_df = topic.get_data_as_df(fields, [], selection_start, selection_end)
    df = pd.concat([df, temp_df])

# Convert index to timestamp column
df = df.reset_index().rename(columns={'_index': 'timestamp'})
`;
}

/**
 * Generates a Python SDK snippet based on file configurations
 */
export function generatePythonSnippet(
  fileConfigs: FileTopicConfig[],
  startTimeSec: number,
  endTimeSec: number,
): string {
  if (fileConfigs.length === 0) {
    return generatePlaceholderSnippet(startTimeSec, endTimeSec);
  }

  if (fileConfigs.length === 1) {
    return generateSingleFileSnippet(fileConfigs[0], startTimeSec, endTimeSec);
  }

  return generateMultiFileSnippet(fileConfigs, startTimeSec, endTimeSec);
}

export type { FileTopicConfig };
